import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

declare global {
  interface Window {
    turnstile?: {
      ready: (callback: () => void) => void
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string
          action?: string
          size?: 'normal' | 'flexible' | 'compact'
          theme?: 'auto' | 'light' | 'dark'
          callback?: (token: string) => void
          'expired-callback'?: () => void
          'error-callback'?: () => void
        }
      ) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

let turnstileLoader: Promise<void> | null = null
let turnstileReadyPromise: Promise<void> | null = null

function loadTurnstileScript() {
  if (turnstileLoader) return turnstileLoader
  turnstileLoader = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile="true"]')
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed to load')), { once: true })
      if (window.turnstile) resolve()
      return
    }

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.dataset.turnstile = 'true'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Turnstile script failed to load'))
    document.head.appendChild(script)
  })
  return turnstileLoader
}

function waitForTurnstileApi() {
  if (turnstileReadyPromise) return turnstileReadyPromise
  turnstileReadyPromise = loadTurnstileScript().then(() => new Promise<void>((resolve, reject) => {
    const startedAt = Date.now()

    const tick = () => {
      const api = window.turnstile
      if (
        typeof api?.render === 'function' &&
        typeof api?.reset === 'function' &&
        typeof api?.remove === 'function'
      ) {
        resolve()
        return
      }
      if (Date.now() - startedAt > 5000) {
        reject(new Error('Turnstile API not ready'))
        return
      }
      window.setTimeout(tick, 50)
    }

    tick()
  }))
  return turnstileReadyPromise
}

export interface TurnstileWidgetHandle {
  reset: () => void
}

interface TurnstileWidgetProps {
  siteKey?: string
  action?: string
  onTokenChange: (token: string | null) => void
  onErrorChange?: (message: string | null) => void
}

const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(function TurnstileWidget(
  { siteKey, action, onTokenChange, onErrorChange },
  ref
) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | null>(null)
  const [size, setSize] = useState<'flexible' | 'compact'>('flexible')

  useEffect(() => {
    if (!containerRef.current) return
    const container = containerRef.current
    const updateSize = () => setSize(container.clientWidth < 300 ? 'compact' : 'flexible')
    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(container)
    return () => observer.disconnect()
  }, [siteKey])

  useImperativeHandle(ref, () => ({
    reset() {
      if (widgetIdRef.current) {
        window.turnstile?.reset(widgetIdRef.current)
      }
      onTokenChange(null)
    },
  }), [onTokenChange])

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      onTokenChange(null)
      onErrorChange?.(null)
      return
    }

    let disposed = false
    onTokenChange(null)
    onErrorChange?.(null)

    waitForTurnstileApi()
      .then(() => {
        if (disposed || !containerRef.current || !window.turnstile) return
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          size,
          theme: 'auto',
          callback: (token) => {
            if (disposed) return
            onErrorChange?.(null)
            onTokenChange(token)
          },
          'expired-callback': () => {
            if (disposed) return
            onTokenChange(null)
            onErrorChange?.('人机验证已过期，请重新完成验证')
          },
          'error-callback': () => {
            if (disposed) return
            onTokenChange(null)
            onErrorChange?.('Cloudflare Turnstile 验证失败，请刷新页面后重试')
          },
        })
      })
      .catch(() => {
        if (disposed) return
        onTokenChange(null)
        onErrorChange?.('Cloudflare Turnstile 脚本加载失败，请检查当前域名或网络配置')
      })

    return () => {
      disposed = true
      if (widgetIdRef.current) {
        window.turnstile?.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [action, onErrorChange, onTokenChange, siteKey, size])

  if (!siteKey) return null

  return <div ref={containerRef} style={{ width: '100%', minWidth: 0 }} />
})

export default TurnstileWidget
